import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Alert, CircularProgress, Fade,
  Chip, IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, Divider, Pagination, Select, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, InputAdornment
} from '@mui/material';
import { Mail, MailOpen, Trash2, RefreshCw, Copy, Check, Inbox, Search } from 'lucide-react';
import { styled } from '@mui/material/styles';
import axios from 'axios';

const StyledTableContainer = styled(TableContainer)(() => ({
  background: '#141926',
  borderRadius: 12,
  border: '1px solid rgba(255,255,255,0.06)',
  '& .MuiTableCell-root': {
    borderBottom: '1px solid rgba(255,255,255,0.04)',
    color: '#D1D5DB',
    padding: '12px 16px',
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    color: '#7C3AED',
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    background: 'rgba(124,58,237,0.04)',
  },
  '& .MuiTableBody-root .MuiTableRow-root': {
    cursor: 'pointer',
    transition: 'background 0.15s',
    '&:hover': { background: 'rgba(124,58,237,0.06)' },
  },
}));

const ITEMS_PER_PAGE = 15;

export default function MeusEmails() {
  const [emails, setEmails] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [copiedAddr, setCopiedAddr] = useState('');
  const [page, setPage] = useState(1);
  const [filterAddr, setFilterAddr] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [addrSearch, setAddrSearch] = useState('');
  const [addrPage, setAddrPage] = useState(1);

  const fetchEmails = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/emails`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setEmails(res.data.emails);
        setAddresses(res.data.addresses);
      }
    } catch {
      setError('Erro ao carregar emails');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchEmails(); }, [fetchEmails]);

  useEffect(() => {
    const interval = setInterval(() => fetchEmails(), 30000);
    return () => clearInterval(interval);
  }, [fetchEmails]);

  const handleOpenEmail = async (email) => {
    setSelectedEmail(email);
    setDialogOpen(true);
    if (!email.read) {
      try {
        const token = localStorage.getItem('token');
        await axios.get(`${process.env.REACT_APP_API_URL}/api/emails/${email._id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setEmails(prev => prev.map(e => e._id === email._id ? { ...e, read: true } : e));
      } catch {}
    }
  };

  const handleDelete = async (emailId, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm('Excluir este email?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/emails/${emailId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmails(prev => prev.filter(em => em._id !== emailId));
      if (selectedEmail?._id === emailId) setDialogOpen(false);
    } catch {}
  };

  const handleCopy = (addr) => {
    navigator.clipboard.writeText(addr);
    setCopiedAddr(addr);
    setTimeout(() => setCopiedAddr(''), 2000);
  };

  const formatDate = (d) => {
    if (!d) return '-';
    const now = new Date();
    const date = new Date(d);
    const diffMs = now - date;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'Agora';
    if (diffMin < 60) return `${diffMin}min atrás`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH}h atrás`;
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatDateFull = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const addrFiltered = filterAddr === 'all' ? emails : emails.filter(e => e.recipient === filterAddr);
  const query = searchQuery.toLowerCase().trim();
  const filteredEmails = query
    ? addrFiltered.filter(e =>
        (e.subject || '').toLowerCase().includes(query) ||
        (e.sender || '').toLowerCase().includes(query) ||
        (e.recipient || '').toLowerCase().includes(query)
      )
    : addrFiltered;
  const totalPages = Math.ceil(filteredEmails.length / ITEMS_PER_PAGE);
  const paginatedEmails = filteredEmails.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);
  const unreadCount = emails.filter(e => !e.read).length;

  const ADDR_PER_PAGE = 20;
  const reversedAddresses = [...addresses].reverse();
  const searchedAddresses = addrSearch.trim()
    ? reversedAddresses.filter(a => a.toLowerCase().includes(addrSearch.toLowerCase().trim()))
    : reversedAddresses;
  const addrTotalPages = Math.ceil(searchedAddresses.length / ADDR_PER_PAGE);
  const visibleAddresses = searchedAddresses.slice((addrPage - 1) * ADDR_PER_PAGE, addrPage * ADDR_PER_PAGE);

  return (
    <Fade in timeout={400}>
      <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1000, mx: 'auto' }}>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ color: '#fff' }}>Caixa de Entrada</Typography>
            <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5 }}>
              Receba emails de verificação da Meta
              {unreadCount > 0 && (
                <Chip label={`${unreadCount} novo${unreadCount > 1 ? 's' : ''}`} size="small"
                  sx={{ ml: 1, height: 20, fontSize: '0.7rem', background: '#7C3AED', color: '#fff' }} />
              )}
            </Typography>
          </Box>
          <Tooltip title="Atualizar">
            <IconButton onClick={() => fetchEmails(true)} sx={{ color: '#7C3AED' }}>
              <RefreshCw size={20} className={refreshing ? 'spin' : ''} />
            </IconButton>
          </Tooltip>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {addresses.length > 0 && (
          <Paper sx={{ p: 2.5, mb: 3, background: '#141926', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 3, overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ color: '#9CA3AF', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                Seus endereços ({addresses.length}) — clique para copiar:
              </Typography>
              <TextField
                size="small"
                placeholder="Buscar endereço..."
                value={addrSearch}
                onChange={(e) => { setAddrSearch(e.target.value); setAddrPage(1); }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Search size={14} color="#6B7280" /></InputAdornment>,
                }}
                sx={{ width: 220, '& .MuiInputBase-input': { fontSize: '0.8rem', py: 0.75 } }}
              />
            </Box>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr 1fr' },
              gap: '10px',
            }}>
              {visibleAddresses.map(addr => (
                <Box key={addr} sx={{ minWidth: 0, overflow: 'hidden' }}>
                  <Chip
                    label={addr}
                    onClick={() => handleCopy(addr)}
                    icon={copiedAddr === addr ? <Check size={14} /> : <Copy size={14} />}
                    size="small"
                    sx={{
                      display: 'flex',
                      width: '100%',
                      maxWidth: '100%',
                      justifyContent: 'flex-start',
                      background: 'rgba(124,58,237,0.1)', color: '#A78BFA',
                      border: '1px solid rgba(124,58,237,0.25)', cursor: 'pointer',
                      fontSize: '0.72rem', height: 36, borderRadius: '8px',
                      '&:hover': { background: 'rgba(124,58,237,0.2)' },
                      '& .MuiChip-icon': { color: copiedAddr === addr ? '#10B981' : '#A78BFA', ml: '8px', flexShrink: 0 },
                      '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', px: 1, minWidth: 0 }
                    }}
                  />
                </Box>
              ))}
            </Box>
            {addrSearch && searchedAddresses.length === 0 && (
              <Typography variant="body2" sx={{ color: '#6B7280', textAlign: 'center', mt: 2, fontSize: '0.8rem' }}>
                Nenhum endereço encontrado
              </Typography>
            )}
            {addrTotalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2, gap: 1 }}>
                <Pagination count={addrTotalPages} page={addrPage} onChange={(_, v) => setAddrPage(v)} size="small"
                  sx={{
                    '& .MuiPaginationItem-root': { color: '#9CA3AF', fontSize: '0.75rem' },
                    '& .Mui-selected': { background: 'rgba(124,58,237,0.2) !important', color: '#A78BFA' }
                  }}
                />
                <Typography variant="caption" sx={{ color: '#4B5563' }}>
                  {searchedAddresses.length} endereço{searchedAddresses.length !== 1 ? 's' : ''}
                </Typography>
              </Box>
            )}
          </Paper>
        )}

        {addresses.length === 0 && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Crie um site primeiro para ter um endereço de email: <strong>nome-do-site@ftmpainel.com.br</strong>
          </Alert>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Buscar por assunto, remetente..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={16} color="#6B7280" />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, minWidth: 200 }}
          />
          {addresses.length > 1 && (
            <Select size="small" value={filterAddr}
              onChange={(e) => { setFilterAddr(e.target.value); setPage(1); }}
              sx={{ minWidth: 200, fontSize: '0.85rem', '& .MuiSelect-select': { py: 0.75 } }}
            >
              <MenuItem value="all">Todos os endereços</MenuItem>
              {reversedAddresses.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
            </Select>
          )}
        </Box>

        {filteredEmails.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Inbox size={48} style={{ color: '#374151', marginBottom: 16 }} />
            <Typography sx={{ color: '#6B7280' }}>
              {searchQuery ? 'Nenhum email encontrado' : 'Nenhum email recebido'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#4B5563', mt: 1 }}>
              {searchQuery ? 'Tente outro termo de busca' : 'Emails enviados para seus endereços aparecerão aqui automaticamente'}
            </Typography>
          </Box>
        ) : (
          <>
            <StyledTableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 40 }}></TableCell>
                    <TableCell>Remetente</TableCell>
                    <TableCell>Assunto</TableCell>
                    <TableCell>Para</TableCell>
                    <TableCell sx={{ width: 100 }}>Data</TableCell>
                    <TableCell sx={{ width: 50 }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedEmails.map(email => (
                    <TableRow key={email._id} onClick={() => handleOpenEmail(email)}
                      sx={{ background: !email.read ? 'rgba(124,58,237,0.04)' : 'transparent' }}>
                      <TableCell>
                        {email.read
                          ? <MailOpen size={16} style={{ color: '#4B5563' }} />
                          : <Mail size={16} style={{ color: '#7C3AED' }} />
                        }
                      </TableCell>
                      <TableCell sx={{
                        fontWeight: email.read ? 400 : 600,
                        color: email.read ? '#9CA3AF' : '#E5E7EB',
                        maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.85rem'
                      }}>
                        {email.sender?.replace(/<.*>/, '').trim() || email.sender}
                      </TableCell>
                      <TableCell sx={{
                        fontWeight: email.read ? 400 : 600,
                        color: email.read ? '#9CA3AF' : '#fff',
                        maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.85rem'
                      }}>
                        {email.subject}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', color: '#6B7280', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {email.recipient}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', color: '#6B7280', whiteSpace: 'nowrap' }}>
                        {formatDate(email.receivedAt)}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Excluir">
                          <IconButton size="small" onClick={(e) => handleDelete(email._id, e)}
                            sx={{ color: '#4B5563', '&:hover': { color: '#EF4444' } }}>
                            <Trash2 size={14} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </StyledTableContainer>

            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)}
                  sx={{
                    '& .MuiPaginationItem-root': { color: '#9CA3AF' },
                    '& .Mui-selected': { background: 'rgba(124,58,237,0.2) !important', color: '#A78BFA' }
                  }}
                />
              </Box>
            )}

            <Typography variant="caption" sx={{ color: '#4B5563', display: 'block', textAlign: 'center', mt: 1 }}>
              {filteredEmails.length} email{filteredEmails.length !== 1 ? 's' : ''}
              {filterAddr !== 'all' && ` em ${filterAddr}`}
              {searchQuery && ` — "${searchQuery}"`}
            </Typography>
          </>
        )}

        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth
          PaperProps={{ sx: { background: '#141926', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 3, maxHeight: '85vh' } }}
        >
          {selectedEmail && (
            <>
              <DialogTitle sx={{ color: '#fff', pb: 0.5, pr: 6 }}>
                <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>{selectedEmail.subject}</Typography>
              </DialogTitle>
              <DialogContent sx={{ pt: '8px !important' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: '#D1D5DB', fontSize: '0.85rem' }}>
                      <span style={{ color: '#6B7280' }}>De:</span> {selectedEmail.sender}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#D1D5DB', fontSize: '0.85rem' }}>
                      <span style={{ color: '#6B7280' }}>Para:</span> {selectedEmail.recipient}
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ color: '#6B7280', whiteSpace: 'nowrap', mt: 0.5 }}>
                    {formatDateFull(selectedEmail.receivedAt)}
                  </Typography>
                </Box>
                <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mb: 2 }} />
                {selectedEmail.htmlBody ? (
                  <Box sx={{
                    background: '#fff', borderRadius: 2, p: 2.5, color: '#111',
                    fontSize: '0.9rem', lineHeight: 1.6,
                    '& a': { color: '#7C3AED' },
                    '& img': { maxWidth: '100%', height: 'auto' },
                    '& table': { maxWidth: '100%' },
                    maxHeight: '55vh', overflow: 'auto',
                  }}>
                    <div dangerouslySetInnerHTML={{ __html: selectedEmail.htmlBody }} />
                  </Box>
                ) : (
                  <Box sx={{ background: 'rgba(255,255,255,0.03)', borderRadius: 2, p: 2.5 }}>
                    <Typography sx={{ color: '#D1D5DB', whiteSpace: 'pre-wrap', fontSize: '0.9rem', lineHeight: 1.7 }}>
                      {selectedEmail.textBody || '(sem conteúdo)'}
                    </Typography>
                  </Box>
                )}
              </DialogContent>
              <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                <Button onClick={(e) => handleDelete(selectedEmail._id, e)} size="small"
                  sx={{ color: '#EF4444', textTransform: 'none' }} startIcon={<Trash2 size={14} />}>
                  Excluir
                </Button>
                <Button onClick={() => setDialogOpen(false)} variant="contained" size="small"
                  sx={{ background: '#7C3AED', textTransform: 'none', '&:hover': { background: '#6D28D9' } }}>
                  Fechar
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>

        <style>{`
          @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
          .spin { animation: spin 1s linear infinite; }
        `}</style>
      </Box>
    </Fade>
  );
}
